import React, { useState } from "react";
import { Box } from "@mui/material";
import { Link } from "react-router-dom";
import Section from "../ui/Section";
import { ArrowLink } from "../ui/Button";
import { Accent, Display, Label } from "../ui/Type";
import { MEDIA } from "../../data/media";
import { SERVICES } from "../../data/services";
import { color, font, motion, type } from "../../design/tokens";

/**
 * Services, as an index rather than a card grid.
 *
 * Nine cards would be nine identical boxes; an index is how a printed
 * catalogue lists nine things, and it reads faster. Each row is a full-width
 * rule-separated link — number, name, one line of substance — and hovering it
 * swaps the plate held in the right column.
 *
 * The preview is decorative and hidden from assistive tech: everything it
 * conveys is already in the row's text. Below `md` it doesn't render at all,
 * and the rows carry their own inline thumbnails instead, because a
 * hover-only affordance is no affordance on a touchscreen.
 *
 * Kitchen has no photograph of our own work yet, so its preview is a
 * typographic panel that says so. Reaching for a stock kitchen to keep the
 * pattern tidy is exactly the shortcut this site is built to avoid.
 */
const ServiceIndex = ({
  title,
  eyebrow = "What we do",
  services = SERVICES,
  intro,
}) => {
  const [active, setActive] = useState(0);
  const activeService = services[active];
  const activeMedia = activeService?.photo ? MEDIA[activeService.photo] : null;

  return (
    <Section tone="paper" id="services">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: { xs: 4, md: 8 },
          alignItems: "end",
          marginBottom: { xs: 6, md: 9 },
        }}
      >
        <Box>
          <Label>{eyebrow}</Label>
          <Display as="h2" size="lg" sx={{ marginTop: 3 }}>
            {title || (
              <>
                Nine trades, <Accent>one crew</Accent>.
              </>
            )}
          </Display>
        </Box>
        {intro ? (
          <Box
            component="p"
            sx={{ ...type.body, color: color.graphite, margin: 0, maxWidth: 460 }}
          >
            {intro}
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.35fr 0.65fr" },
          columnGap: { md: 8 },
          alignItems: "start",
        }}
      >
        {/* ── Index ─────────────────────────────────────────────────────── */}
        <Box sx={{ borderTop: `1px solid ${color.line}` }}>
          {services.map((service, index) => (
            <Box
              key={service.slug}
              component={Link}
              to={`/services/${service.slug}`}
              onMouseEnter={() => setActive(index)}
              onFocus={() => setActive(index)}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "auto 1fr auto", md: "auto 1fr auto" },
                alignItems: "center",
                columnGap: { xs: 2, md: 4 },
                paddingBlock: { xs: 2.5, md: 3.25 },
                borderBottom: `1px solid ${color.line}`,
                transition: `background-color ${motion.base} ${motion.ease}`,
                "&:hover .flr-index-title, &:focus-visible .flr-index-title": {
                  color: color.clay,
                  transform: "translateX(8px)",
                },
                "&:hover .flr-index-arrow, &:focus-visible .flr-index-arrow": {
                  opacity: 1,
                  transform: "translateX(0)",
                },
              }}
            >
              <Box
                component="span"
                aria-hidden="true"
                sx={{ ...type.numeral, color: color.mute, minWidth: 24 }}
              >
                {String(index + 1).padStart(2, "0")}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, minWidth: 0 }}>
                {/* Inline thumbnail — the mobile stand-in for the hover
                    preview. Decorative; the row text carries the meaning. */}
                {service.photo ? (
                  <Box
                    aria-hidden="true"
                    component="img"
                    src={MEDIA[service.photo].src}
                    alt=""
                    sx={{
                      display: { xs: "block", md: "none" },
                      width: 52,
                      height: 52,
                      objectFit: "cover",
                      flexShrink: 0,
                    }}
                  />
                ) : null}

                <Box sx={{ minWidth: 0 }}>
                  <Box
                    className="flr-index-title"
                    component="span"
                    sx={{
                      display: "block",
                      fontFamily: font.display,
                      fontSize: { xs: "1.5rem", md: "2rem" },
                      lineHeight: 1.15,
                      color: color.ink,
                      transition: `color ${motion.base} ${motion.ease}, transform ${motion.base} ${motion.ease}`,
                    }}
                  >
                    {service.name}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      display: "block",
                      ...type.bodySm,
                      color: color.mute,
                      marginTop: 0.75,
                      maxWidth: 460,
                    }}
                  >
                    {service.summary}
                  </Box>
                </Box>
              </Box>

              <Box
                className="flr-index-arrow"
                component="svg"
                aria-hidden="true"
                viewBox="0 0 16 16"
                sx={{
                  width: 16,
                  height: 16,
                  fill: "none",
                  stroke: color.clay,
                  strokeWidth: 1.5,
                  opacity: { xs: 0.4, md: 0 },
                  transform: { md: "translateX(-8px)" },
                  transition: `opacity ${motion.base} ${motion.ease}, transform ${motion.base} ${motion.ease}`,
                }}
              >
                <path d="M1 8h13M9 3l5 5-5 5" />
              </Box>
            </Box>
          ))}
        </Box>

        {/* ── Hover preview ─────────────────────────────────────────────── */}
        <Box
          aria-hidden="true"
          sx={{
            display: { xs: "none", md: "block" },
            position: "sticky",
            top: 140,
            marginTop: 2,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              aspectRatio: "4 / 5",
              backgroundColor: color.paperEdge,
              overflow: "hidden",
            }}
          >
            {services.map((service, index) => {
              const media = service.photo ? MEDIA[service.photo] : null;
              const isActive = index === active;

              return media ? (
                <Box
                  key={service.slug}
                  component="img"
                  src={media.src}
                  alt=""
                  loading="lazy"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    opacity: isActive ? 1 : 0,
                    transition: `opacity ${motion.slow} ${motion.ease}`,
                  }}
                />
              ) : (
                // Honest placeholder for the one service with no photo of our
                // own work yet.
                <Box
                  key={service.slug}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "grid",
                    placeItems: "center",
                    padding: 4,
                    textAlign: "center",
                    backgroundColor: color.paperEdge,
                    opacity: isActive ? 1 : 0,
                    transition: `opacity ${motion.slow} ${motion.ease}`,
                  }}
                >
                  <Box>
                    <Box component="p" sx={{ ...type.label, color: color.mute, justifyContent: "center" }}>
                      {service.title}
                    </Box>
                    <Box
                      component="p"
                      sx={{
                        fontFamily: font.display,
                        fontSize: "1.35rem",
                        lineHeight: 1.3,
                        color: color.graphite,
                        marginTop: 2,
                      }}
                    >
                      We don't have a photograph of one of our own kitchens yet —
                      so there isn't one here.
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Box
            component="p"
            sx={{
              ...type.caption,
              color: color.mute,
              marginTop: 1.75,
              paddingLeft: 2,
              borderLeft: `1px solid ${color.line}`,
              minHeight: 40,
            }}
          >
            {activeMedia ? activeMedia.caption : `${activeService?.name} — photography to come`}
          </Box>

          <Box sx={{ marginTop: 4 }}>
            <ArrowLink to="/services">All services</ArrowLink>
          </Box>
        </Box>
      </Box>
    </Section>
  );
};

export default ServiceIndex;
